import React, { useState, useRef, useEffect } from "react";
import "./loginpage.css";

export default function LoginPage({
  mode,
  onLogin,
  onRegister,
  onSwitchToRegister,
  onBackToLogin,
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayname, setDisplayname] = useState("");
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");

  // Password validation state
  const [passwordChecks, setPasswordChecks] = useState({
    length: false,
    upper: false,
    lower: false,
    number: false,
    symbol: false,
  });

  const emailRef = useRef(null);
  const displaynameRef = useRef(null);

  useEffect(() => {
    if (mode === "register" && displaynameRef.current) {
      displaynameRef.current.focus();
    } else if (mode === "login" && emailRef.current) {
      emailRef.current.focus();
    }
  }, [mode]);

  // Validate password live
  useEffect(() => {
    setPasswordChecks({
      length: password.length >= 6,
      upper: /[A-Z]/.test(password),
      lower: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      symbol: /[!@#$%^&*()_+\-=[\]{};':"\\|<>?,./`~]/.test(password),
    });
  }, [password]);

  const isPasswordValid = Object.values(passwordChecks).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setFormError("");

    // Block bad password before hitting backend
    if (mode === "register" && !isPasswordValid) {
      setFormError("Please create a stronger password before continuing.");
      return;
    }

    setLoading(true);

    try {
      if (mode === "login") {
        await onLogin(email, password);
      } else if (mode === "register") {
        await onRegister(email, password, displayname);
      }
    } catch (err) {
      setFormError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <img
          className="login-logo"
          src="/Logos/Hobbliclearbackground.png"
          alt="Hobbli Logo"
        />

        <h4>
          You are welcome to login/register, but please understand this is a work in progress.
        </h4>

        <form className="login-form" onSubmit={handleSubmit}>
          {mode === "register" && (
            <>
              <label htmlFor="displayname">Display Name</label>
              <input
                type="text"
                id="displayname"
                ref={displaynameRef}
                value={displayname}
                onChange={(e) => setDisplayname(e.target.value)}
                required
              />
            </>
          )}

          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            ref={emailRef}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />

          <label htmlFor="password">Password</label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />

          {/* Password checklist (only show on register) */}
          {mode === "register" && (
            <div className="password-checklist">
              <p>Password must include:</p>
              <ul>
                <li className={passwordChecks.length ? "valid" : "invalid"}>
                  {passwordChecks.length ? "✔" : "✖"} At least 6 characters
                </li>
                <li className={passwordChecks.upper ? "valid" : "invalid"}>
                  {passwordChecks.upper ? "✔" : "✖"} Uppercase letter
                </li>
                <li className={passwordChecks.lower ? "valid" : "invalid"}>
                  {passwordChecks.lower ? "✔" : "✖"} Lowercase letter
                </li>
                <li className={passwordChecks.number ? "valid" : "invalid"}>
                  {passwordChecks.number ? "✔" : "✖"} Number
                </li>
                <li className={passwordChecks.symbol ? "valid" : "invalid"}>
                  {passwordChecks.symbol ? "✔" : "✖"} Symbol
                </li>
              </ul>
            </div>
          )}

          {formError ? (
            <div role="alert" style={{ marginBottom: "12px", padding: "10px 12px", borderRadius: "8px", background: "rgba(220, 53, 69, 0.10)", color: "#b42318", fontSize: ".88rem" }}>
              {formError}
            </div>
          ) : null}

          <button
            type="submit"
            className="btn-primary"
            disabled={loading || (mode === "register" && !isPasswordValid)}
          >
            {loading
              ? mode === "login"
                ? "Logging in..."
                : "Registering..."
              : mode === "login"
              ? "Login"
              : "Register"}
          </button>
        </form>

        <div className="login-toggle">
          {mode === "login" ? (
            <p>
              Don't have an account?{" "}
              <button type="button" className="btn-link" onClick={onSwitchToRegister}>
                Register here
              </button>
            </p>
          ) : (
            <p>
              Already have an account?{" "}
              <button type="button" className="btn-link" onClick={onBackToLogin}>
                Login here
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}